/**
 * Cookieless landing-visit attribution — pure, universal (browser + server +
 * edge) helpers backing the `/ig` bio-link tracker.
 *
 * NOT the same system as `./traffic-attribution.ts` (client-only, localStorage
 * first/last-touch multi-visit engine that feeds the pre-existing signup
 * `attribution` user_metadata field + Vercel Analytics custom events). This
 * module serves the newer server-truth pipeline: VisitBeacon component ->
 * POST /api/track/visit -> `landing_visits` table, and its funnel carry into
 * `signup_attributions` / demo-lead `custom_fields.utm_attribution` via the
 * `cmg_attribution` sessionStorage key. The two systems intentionally
 * coexist — this one is queryable server-side for /admin/traffic; the other
 * is not.
 *
 * Uses only Web Crypto (`globalThis.crypto.subtle`) — never `node:crypto` —
 * so this file is safe to import from a 'use client' component (VisitBeacon)
 * without pulling a Node builtin into the browser bundle.
 */

export interface ParsedAttribution {
  source: string;
  medium: string | null;
  campaign: string | null;
  refCode: string | null;
}

/**
 * `landing_attribution` payload shape stored in `signUp()` user_metadata and
 * validated server-side by `landingAttributionSchema`
 * (src/app/api/auth/callback/route.ts). Extends the parsed attribution with
 * the visit/visitor identifiers RegisterForm merges in at signup time
 * (`visitId` from the `cmg_visit_id` sessionStorage key, `visitorId` from the
 * consent-gated `cmg_vid` cookie) — this module never populates them itself,
 * it only carries the type.
 */
export interface LandingAttributionPayload extends ParsedAttribution {
  visitId?: string;
  visitorId?: string;
}

/**
 * sessionStorage key VisitBeacon writes to (when the landing URL carries
 * utm_/ref params) and WhatsAppDemoForm / RegisterForm read from to stamp
 * the funnel's later steps (demo lead, org signup).
 */
export const ATTRIBUTION_STORAGE_KEY = 'cmg_attribution';

/** Known short ref codes -> canonical source name (e.g. the Instagram bio link `?ref=ig`). */
const REF_CODE_SOURCE_MAP: Readonly<Record<string, string>> = {
  ig: 'instagram',
};

/** Referrer-hostname -> source, checked in order; first match wins. */
const REFERRER_SOURCE_RULES: ReadonlyArray<{ test: RegExp; source: string }> = [
  { test: /(^|\.)instagram\.com$/i, source: 'instagram' },
  { test: /(^|\.)tiktok\.com$/i, source: 'tiktok' },
  { test: /(^|\.)t\.co$|(^|\.)twitter\.com$|(^|\.)x\.com$/i, source: 'x' },
  { test: /(^|\.)facebook\.com$|(^|\.)fb\.com$/i, source: 'facebook' },
  { test: /(^|\.)google\./i, source: 'google-organic' },
];

function hostnameOf(href: string): string | null {
  try {
    return new URL(href).hostname.toLowerCase();
  } catch {
    // intentional: malformed/relative referrer string — treat as no host
    return null;
  }
}

function deriveSourceFromReferrer(referrer: string | null): string {
  const host = referrer ? hostnameOf(referrer) : null;
  if (!host) return 'direct';
  for (const rule of REFERRER_SOURCE_RULES) {
    if (rule.test.test(host)) return rule.source;
  }
  return 'direct';
}

/**
 * Derive `{source, medium, campaign, refCode}` from a landing URL + referrer.
 *
 * Precedence for `source`: `utm_source` > `ref` (mapped via REF_CODE_SOURCE_MAP,
 * unknown codes pass through as the raw source) > referrer-derived host match >
 * `'direct'`. `medium`/`campaign`/`refCode` are read straight off the query
 * string — they are not ambiguous the way `source` is.
 */
export function parseAttribution(url: URL, referrer: string | null): ParsedAttribution {
  const params = url.searchParams;
  const utmSource = params.get('utm_source')?.trim() || null;
  const utmMedium = params.get('utm_medium')?.trim() || null;
  const utmCampaign = params.get('utm_campaign')?.trim() || null;
  const refParam = params.get('ref')?.trim() || null;

  let source: string;
  if (utmSource) {
    source = utmSource;
  } else if (refParam) {
    source = REF_CODE_SOURCE_MAP[refParam.toLowerCase()] ?? refParam;
  } else {
    source = deriveSourceFromReferrer(referrer);
  }

  return { source, medium: utmMedium, campaign: utmCampaign, refCode: refParam };
}

/** True when the landing URL carries attribution params worth persisting for the funnel carry. */
export function hasAttributionParams(url: URL): boolean {
  const p = url.searchParams;
  return Boolean(
    p.get('utm_source') || p.get('ref') || p.get('utm_medium') || p.get('utm_campaign')
  );
}

/**
 * Safely read the funnel-carry attribution written by VisitBeacon to
 * sessionStorage. Returns null on any missing/malformed/unavailable state —
 * callers treat attribution as always-optional (cross-tab flows, private
 * browsing, or a visitor who never saw a tracked link legitimately have none).
 */
export function readStoredAttribution(): ParsedAttribution | null {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ParsedAttribution> | null;
    if (!parsed || typeof parsed.source !== 'string' || !parsed.source) return null;
    return {
      source: parsed.source,
      medium: typeof parsed.medium === 'string' ? parsed.medium : null,
      campaign: typeof parsed.campaign === 'string' ? parsed.campaign : null,
      refCode: typeof parsed.refCode === 'string' ? parsed.refCode : null,
    };
  } catch {
    // intentional: sessionStorage unavailable / malformed JSON — no attribution to carry
    return null;
  }
}

// =============================================================================
// BOT DETECTION
// =============================================================================

const BOT_UA_PATTERN =
  /bot|crawl|spider|headless|lighthouse|preview|facebookexternalhit|whatsapp|telegram|slack|discord|pingdom|uptime/i;

/** Heuristic UA-based bot/crawler/monitor detection for beacon traffic. */
export function isBotUA(ua: string | null | undefined): boolean {
  if (!ua) return false;
  return BOT_UA_PATTERN.test(ua);
}

// =============================================================================
// VISITOR HASH (cookieless, daily-rotating)
// =============================================================================

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Env fallback for the daily hash salt: `TRACKING_HASH_SECRET`, else the
 * existing `CRON_SECRET` (already a private, rotatable secret in every env),
 * else `''` (degrades to a still-daily-rotating but publicly-guessable salt —
 * acceptable for a best-effort uniques counter, never a security boundary).
 */
export function resolveTrackingHashSecret(): string {
  return process.env.TRACKING_HASH_SECRET || process.env.CRON_SECRET || '';
}

/**
 * Daily-rotating, non-reversible visitor hash: `sha256(sha256(secret+YYYY-MM-DD)+ip+ua)`.
 * Same visitor, same calendar day -> stable hash (used for the 7d "uniques" count).
 * A new day always produces an unrelated hash — no cross-day tracking, no cookie,
 * nothing that identifies a person beyond same-day de-duplication.
 */
export async function computeVisitorHash(
  ip: string,
  ua: string,
  dateStr: string,
  secret: string
): Promise<string> {
  const dailySalt = await sha256Hex(secret + dateStr);
  return sha256Hex(dailySalt + ip + ua);
}
